import { createContext, useContext, useMemo, type ReactNode } from 'react'
import type { AnyRecordAuthorization, AskedRecord } from './useRecordAuthorization'
import type { PermissionSetEntity } from '@omnea-app/authz'

type ProvidedRecordAuthorization = AskedRecord<PermissionSetEntity> & { authorization: AnyRecordAuthorization }

const RecordAuthorizationContext = createContext<ProvidedRecordAuthorization | undefined>(undefined)

type Props = {
  entity: PermissionSetEntity
  /** Null until a page knows which record it is showing, which offers nothing rather than remounting. */
  recordId: string | null
  authorization: AnyRecordAuthorization | null
  children: ReactNode
}

/** The record answer a page already holds, offered to the affordances on it. */
export const RecordAuthorizationProvider = ({ entity, recordId, authorization, children }: Props) => {
  const provided = useMemo(
    () => (recordId == null || authorization == null ? undefined : { entity, recordId, authorization }),
    [entity, recordId, authorization]
  )

  return <RecordAuthorizationContext.Provider value={provided}>{children}</RecordAuthorizationContext.Provider>
}

/**
 * The provided answer, only where it is for the record being asked about — a component showing a
 * second record of the same family must not be handed the page's.
 */
export const useProvidedRecordAuthorization = (
  asked: AskedRecord<PermissionSetEntity> | null
): AnyRecordAuthorization | undefined => {
  const provided = useContext(RecordAuthorizationContext)
  if (provided == null || asked == null) return undefined

  return provided.entity === asked.entity && provided.recordId === asked.recordId ? provided.authorization : undefined
}
